import { useContext, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { MyContext } from '../Store';
import { UploadImage } from './UploadImage';
import { getUrlSearchParams, draggableConfig, resizableConfig, pxToPercentage, percentageToPx } from '../utils';
import { Marking, MarkingArrows } from './index';
import { debounce } from 'lodash';

const urlParams = getUrlSearchParams();
const forPdf = urlParams.get('forPdf') === 'true';
const pdfTimestamp = Date.now();

const getDate = () => {
  if (forPdf) {
    return pdfTimestamp;
  }
  return Date.now();
}

export const MarkingZone = ({ width, height }) => {
  const [state, dispatch] = useContext(MyContext);
  const { selectedMarking } = state;

  const zoneRef = useRef(null);
  const imgRef = useRef(null);

  const [imgSrc, setImgSrc] = useState(selectedMarking.markingImg?.src || '');

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const [isMarkingVisible, setIsMarkingVisible] = useState(false);
  const [markingPosition, setMarkingPosition] = useState({ x: 0, y: 0 });
  const [markingTransform, setMarkingTransform] = useState('');
  const [markingWidth, setMarkingWidth] = useState(0);
  const [markingHeight, setMarkingHeight] = useState(0);
  const [markingRotate, setMarkingRotate] = useState(selectedMarking.markingImg?.rotate || 0);

  const getMarkingRotate = () => (markingRotate > 0 ? `rotate(${markingRotate}deg)` : '');

  const updateMarkingImg = newData => {
    const markingImg = { ...selectedMarking.markingImg, ...newData };
    const payload = { ...selectedMarking, markingImg };
    dispatch({ type: 'SET_SELECTED_MARKING', payload });
  };

  const debounceUpdateMarkingImg = debounce((x, y, parent, width = null, height = null) => {
    parent.style.backgroundColor = 'transparent';

    const newData = {};
    newData.position = {
      x: pxToPercentage(x, 'x', parent),
      y: pxToPercentage(y, 'y', parent),
    };
    if (width) {
      newData.width = pxToPercentage(width, 'x', parent);
    }
    if (height) {
      newData.height = pxToPercentage(height, 'y', parent);
    }
    updateMarkingImg(newData);
  }, 300);

  const dragMoveListener = event => {
    const target = event.target;
    // keep the dragged position in the data-x/data-y attributes
    let { x, y } = target.dataset;
    x = (parseFloat(x) || 0) + event.dx;
    y = (parseFloat(y) || 0) + event.dy;

    Object.assign(target.style, { transform: `translate(${x}px, ${y}px) ${getMarkingRotate()}` });
    Object.assign(target.dataset, { x, y });
    debounceUpdateMarkingImg(x, y, target.parentNode);
  };

  const draggableOptions = {
    onstart: event => (event.target.parentNode.style.backgroundColor = 'rgba(0, 255, 0, 0.2)'),
    onmove: dragMoveListener,
    onend: event => (event.target.parentNode.style.backgroundColor = 'transparent'),
    ...draggableConfig,
  };

  const resizeMoveListener = event => {
    const target = event.target;
    const isInverted = [90, 270].includes(markingRotate);

    // keep the dragged position in the data-x/data-y attributes
    let { x, y } = target.dataset;
    x = parseFloat(x) || 0;
    y = parseFloat(y) || 0;

    // marking rotation position fix
    if (isInverted) {
      x -= event.deltaRect.height / 2;
      x += (event.deltaRect.right || event.deltaRect.left) / 2;
      y -= event.deltaRect.width / 2;
      y += (event.deltaRect.bottom || event.deltaRect.top) / 2;
    } else {
      x += event.deltaRect.left;
      y += event.deltaRect.top;
    }

    // marking rotation width and height fix
    const width = isInverted ? event.rect.height : event.rect.width;
    const height = isInverted ? event.rect.width : event.rect.height;

    Object.assign(target.style, {
      width: `${width}px`,
      height: `${height}px`,
      transform: `translate(${x}px, ${y}px) ${getMarkingRotate()}`,
    });
    Object.assign(target.dataset, { x, y });
    debounceUpdateMarkingImg(x, y, target.parentNode, width, height);
  };

  const resizableOptions = {
    onmove: resizeMoveListener,
    ...resizableConfig,
  };

  const setMarkingData = useCallback(
    (width, height) => {
      const x = percentageToPx(selectedMarking.markingImg?.position?.x || 0, 'x', width, height);
      const y = percentageToPx(selectedMarking.markingImg?.position?.y || 0, 'y', width, height);

      setMarkingPosition({ x, y });
      setMarkingTransform(`translate(${x}px, ${y}px)`);

      if (selectedMarking.markingImg?.width) {
        setMarkingWidth(percentageToPx(selectedMarking.markingImg.width, 'x', width, height));
      }
      if (selectedMarking.markingImg?.height) {
        setMarkingHeight(percentageToPx(selectedMarking.markingImg.height, 'y', width, height));
      }
      setIsMarkingVisible(true);
    },
    [selectedMarking],
  );

  const debounceSetImgRatio = debounce(uploadedImg => {
    let ratio = 1;
    const parent = zoneRef.current?.parentNode;

    if (imgRef.current.naturalWidth >= imgRef.current.naturalHeight) {
      ratio = imgRef.current.naturalHeight / imgRef.current.naturalWidth;
      const height = ratio * imgRef.current.offsetWidth;

      setMarkingHeight(height);
      updateMarkingImg({
        src: uploadedImg,
        width: pxToPercentage(imgRef.current.offsetWidth, 'x', parent),
        height: pxToPercentage(height, 'y', parent),
      });
    } else {
      ratio = imgRef.current.naturalWidth / imgRef.current.naturalHeight;
      const width = ratio * imgRef.current.offsetHeight;

      setMarkingWidth(width);
      updateMarkingImg({
        src: uploadedImg,
        width: pxToPercentage(width, 'x', parent),
        height: pxToPercentage(imgRef.current.offsetHeight, 'y', parent),
      });
    }
  }, 150);

  const setMarkingImage = uploadedImg => {
    setImgSrc(uploadedImg);
    debounceSetImgRatio(uploadedImg);
  };

  const handleRotate = () => {
    const rotateValue = markingRotate < 270 ? markingRotate + 90 : 0;
    setMarkingRotate(rotateValue);
    updateMarkingImg({ rotate: rotateValue });
  };

  const handleUpload = () => setIsUploadModalOpen(true);

  const handleRemove = () => dispatch({ type: 'SET_SELECTED_MARKING', payload: {} });

  const markingButtons = [
    { className: 'rotate', iconClassName: 'fa-rotate', callback: () => handleRotate() },
    { className: 'resize', iconClassName: 'fa-up-right-and-down-left-from-center fa-rotate-90', callback: () => null },
    { className: 'upload', iconClassName: 'fa-upload', callback: () => handleUpload() },
    { className: 'remove', iconClassName: 'fa-trash', callback: () => handleRemove() },
  ];

  const showMarking = !forPdf || (forPdf && selectedMarking?.markingImg?.src);

  useLayoutEffect(() => {
    const observer = new ResizeObserver(entries => {
      const el = entries[0].contentRect;

      if (el.width > 0 && el.height > 0) {
        setMarkingData(el.width, el.height);
      }
    });
    observer.observe(zoneRef.current);

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    return () => zoneRef.current && observer.unobserve(zoneRef.current);
  }, [setMarkingData]);

  return (
    <>
      <div ref={zoneRef} className="marking-zone" style={{ width: width, height: height }}>
        {forPdf && (
          <MarkingArrows
            markingImg={selectedMarking.markingImg}
            markingPosition={markingPosition}
            markingWidth={markingWidth || 142}
            markingHeight={markingHeight || 120}
            markingRotate={markingRotate}
            markingZoneWidth={width}
            markingZoneHeight={height}
          />
        )}
        {showMarking && (
          <Marking
            draggable={!forPdf}
            draggableOptions={draggableOptions}
            resizable={!forPdf}
            resizableOptions={resizableOptions}
          >
            <div
              className={`marking marking-rotate-${markingRotate}`}
              data-x={markingPosition.x}
              data-y={markingPosition.y}
              style={{
                transform: `${markingTransform} ${getMarkingRotate()}`,
                width: markingWidth ? `${markingWidth}px` : '142px',
                height: markingHeight ? `${markingHeight}px` : '120px',
                display: isMarkingVisible ? 'block' : 'none',
              }}
            >
              <img
                src={imgSrc ? `${imgSrc}?${getDate()}` : `${process.env.PUBLIC_URL}/upload-logo.png`}
                alt=""
                style={{ width: '100%', height: '100%', cursor: imgSrc ? 'inherit' : 'pointer' }}
                ref={imgRef}
                onClick={imgSrc ? () => null : () => setIsUploadModalOpen(true)}
              ></img>
              {!forPdf &&
                markingButtons.map(btn => (
                  <button
                    type="button"
                    className={`marking-btn ${btn.className}`}
                    style={{ transform: `rotate(${-markingRotate}deg)` }}
                    onClick={btn.callback}
                    key={btn.className}
                  >
                    <i className={`fa-solid ${btn.iconClassName}`}></i>
                  </button>
                ))}
            </div>
          </Marking>
        )}
      </div>
      {isUploadModalOpen && (
        <UploadImage setIsUploadModalOpen={setIsUploadModalOpen} setMarkingImage={setMarkingImage} />
      )}
    </>
  );
};
